<script setup>
import { ref } from 'vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  // Collapsible sections start closed: advanced knobs stay out of the way
  // until someone asks for them.
  collapsible: {
    type: Boolean,
    default: false,
  },
});

const isOpen = ref(!props.collapsible);

const toggle = () => {
  if (props.collapsible) isOpen.value = !isOpen.value;
};
</script>

<template>
  <section
    class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
  >
    <header
      v-if="title || $slots.actions"
      class="flex flex-wrap items-start justify-between gap-3 px-5 pt-5"
      :class="{
        'cursor-pointer select-none': collapsible,
        'pb-5': collapsible && !isOpen,
      }"
      :data-testid="collapsible ? 'studio-section-toggle' : undefined"
      @click="toggle"
    >
      <div class="flex items-start gap-3 min-w-0">
        <span
          v-if="icon"
          class="flex items-center justify-center shrink-0 rounded-lg size-8 bg-n-alpha-2 text-n-slate-11"
        >
          <span :class="icon" class="size-4" />
        </span>
        <div class="flex flex-col min-w-0 gap-0.5">
          <h3 class="text-heading-3 text-n-slate-12">{{ title }}</h3>
          <p v-if="description" class="text-body-main text-n-slate-11">
            {{ description }}
          </p>
        </div>
      </div>
      <div
        v-if="$slots.actions || collapsible"
        class="flex items-center gap-2 shrink-0"
      >
        <slot name="actions" />
        <span
          v-if="collapsible"
          class="i-lucide-chevron-down size-5 text-n-slate-10 transition-transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </div>
    </header>
    <div v-show="isOpen" class="flex flex-col gap-4 p-5">
      <slot />
    </div>
  </section>
</template>
